import { extractVin } from "./vin-text"
import type { VinWorkerAnswer, VinWorkerRequest } from "./vin-worker"

// Reads a VIN barcode from the live camera, on the phone. The door sticker
// (and most insurance cards) carry the VIN as a barcode: Code 39 / Code 128
// on stickers, Data Matrix / PDF417 / QR on cards. Chrome on Android has a
// built-in reader (BarcodeDetector); everywhere else (iPhone Safari) the
// open-source ZXing reader (Apache-2.0) is loaded, only for this step, and
// runs in a worker so the camera never stutters.

export interface VinReader {
  // Looks once at the middle of the picture; the VIN, or null.
  read(video: HTMLVideoElement): Promise<string | null>
}

const FORMATS = ["code_39", "code_128", "data_matrix", "pdf417", "qr_code"]
const MAX_WIDTH = 1280

type Detector = { detect(source: CanvasImageSource): Promise<Array<{ rawValue: string }>> }
type DetectorCtor = {
  new (opts: { formats: string[] }): Detector
  getSupportedFormats(): Promise<string[]>
}

// The middle band of what's on screen, where people put the sticker.
function grabMiddle(video: HTMLVideoElement, canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  const vw = video.videoWidth
  const vh = video.videoHeight
  if (!vw || !vh) return null
  const sw = vw * 0.9
  const sh = Math.min(vh * 0.55, sw)
  const scale = Math.min(1, MAX_WIDTH / sw)
  canvas.width = Math.round(sw * scale)
  canvas.height = Math.round(sh * scale)
  const ctx = canvas.getContext("2d", { willReadFrequently: true })
  if (!ctx) return null
  ctx.drawImage(video, (vw - sw) / 2, (vh - sh) / 2, sw, sh, 0, 0, canvas.width, canvas.height)
  return ctx
}

async function nativeReader(): Promise<VinReader | null> {
  const Ctor = (globalThis as unknown as { BarcodeDetector?: DetectorCtor }).BarcodeDetector
  if (!Ctor) return null
  try {
    const supported = await Ctor.getSupportedFormats()
    const formats = FORMATS.filter((f) => supported.includes(f))
    if (!formats.includes("code_39") && !formats.includes("code_128")) return null
    const detector = new Ctor({ formats })
    const canvas = document.createElement("canvas")
    return {
      async read(video) {
        if (!grabMiddle(video, canvas)) return null
        try {
          for (const code of await detector.detect(canvas)) {
            const vin = extractVin(code.rawValue)
            if (vin) return vin
          }
        } catch {
          // A frame it couldn't read: the next one may.
        }
        return null
      },
    }
  } catch {
    return null
  }
}

// ZXing runs in a worker (vin-worker.ts); this side only grabs the frame.
async function zxingReader(): Promise<VinReader | null> {
  if (typeof Worker === "undefined") return null
  let worker: Worker
  try {
    worker = new Worker(new URL("./vin-worker.ts", import.meta.url), { type: "module" })
  } catch {
    return null
  }
  const canvas = document.createElement("canvas")
  const waiting = new Map<number, (text: string | null) => void>()
  let next = 0
  worker.onmessage = (e: MessageEvent<VinWorkerAnswer>) => {
    waiting.get(e.data.id)?.(e.data.text)
    waiting.delete(e.data.id)
  }
  // A worker that fails to start answers nothing: let the reads end.
  worker.onerror = () => {
    for (const done of waiting.values()) done(null)
    waiting.clear()
  }
  return {
    async read(video) {
      const ctx = grabMiddle(video, canvas)
      if (!ctx) return null
      const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const id = ++next
      const text = await new Promise<string | null>((resolve) => {
        waiting.set(id, resolve)
        const request: VinWorkerRequest = { id, rgba: data, width: canvas.width, height: canvas.height }
        worker.postMessage(request, [data.buffer])
      })
      return text ? extractVin(text) : null
    },
  }
}

let loading: Promise<VinReader | null> | null = null

export function loadVinReader(): Promise<VinReader | null> {
  if (!loading) {
    loading = (async () => (await nativeReader()) ?? (await zxingReader()))()
    loading.then((r) => {
      if (!r) loading = null
    })
  }
  return loading
}
